import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle, Loader2 } from 'lucide-react'
import { Button } from './Button'
import { LocalizedLink } from '@/components/LocalizedLink'
import { apiUrl } from '@/lib/api'
import { cn } from '@/lib/utils'

interface BookingFormProps {
  serviceOptions: { value: string; label: string }[]
}

function toDateInputValue(date: Date) {
  return date.toISOString().split('T')[0]
}

function isWeekend(dateString: string) {
  const d = new Date(dateString + 'T00:00:00')
  const day = d.getDay()
  return day === 0 || day === 6
}

function getNextWeekday(date: Date): Date {
  const d = new Date(date)
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() + 1)
  }
  return d
}

function getTimeSlots(date: string | undefined): string[] {
  if (!date) return []
  const d = new Date(date + 'T00:00:00')
  const day = d.getDay()
  // Sunday = 0, Saturday = 6
  if (day === 0 || day === 6) return []
  const slots: string[] = []
  for (let hour = 9; hour <= 18; hour++) {
    slots.push(`${String(hour).padStart(2, '0')}:00`)
  }
  return slots
}

export function BookingForm({ serviceOptions }: BookingFormProps) {
  const { t } = useTranslation(['booking', 'common'])

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [service, setService] = useState('')
  const [preferredDate, setPreferredDate] = useState('')
  const [preferredTime, setPreferredTime] = useState('')
  const [notes, setNotes] = useState('')
  const [privacy, setPrivacy] = useState(false)

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [dateError, setDateError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const minDate = useMemo(() => toDateInputValue(getNextWeekday(new Date())), [])

  const timeSlots = useMemo(
    () => getTimeSlots(preferredDate),
    [preferredDate]
  )

  useEffect(() => {
    if (timeSlots.length > 0 && preferredTime && !timeSlots.includes(preferredTime)) {
      setPreferredTime('')
    }
  }, [timeSlots, preferredTime])

  const validate = () => {
    const nextErrors: Record<string, string> = {}
    if (!name.trim()) {
      nextErrors.name = t('booking:form.validation.nameRequired')
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = t('booking:form.validation.emailInvalid')
    }
    if (!privacy) {
      nextErrors.privacy = t('booking:form.validation.privacyRequired')
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    if (!validate()) return

    setSubmitting(true)
    try {
      const res = await fetch(apiUrl('/api/bookings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          service: service || undefined,
          preferredDate: preferredDate || undefined,
          preferredTime: preferredTime || undefined,
          notes: notes.trim() || undefined,
        }),
      })
      if (!res.ok) throw new Error('Booking failed')
      setSuccess(true)
    } catch {
      setSubmitError(t('booking:form.error.message'))
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => {
    setName('')
    setEmail('')
    setPhone('')
    setService('')
    setPreferredDate('')
    setPreferredTime('')
    setNotes('')
    setPrivacy(false)
    setErrors({})
    setSubmitError(null)
    setSuccess(false)
  }

  const inputBase =
    'w-full min-w-0 max-w-full rounded-lg border bg-ink-deep/60 px-4 py-3 text-white placeholder:text-white/30 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors'
  const inputError = 'border-red-400/60 focus:border-red-400 focus:ring-red-400'
  const inputNormal = 'border-white/10 focus:border-primary'

  if (success) {
    return (
      <div className="rounded-2xl border border-primary/20 bg-ink p-8 md:p-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-6">
          <CheckCircle className="w-8 h-8" />
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-white">
          {t('booking:form.success.title')}
        </h3>
        <p className="mt-3 text-white/70 max-w-md mx-auto">
          {t('booking:form.success.message')}
        </p>
        <div className="mt-8">
          <Button type="button" onClick={reset}>
            {t('common:buttons.send')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="min-w-0 rounded-2xl border border-white/10 bg-ink p-6 md:p-8 lg:p-10">
      <h2 className="font-display text-2xl md:text-3xl text-white mb-6">
        {t('booking:form.title')}
      </h2>

      <div className="grid min-w-0 grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="min-w-0 md:col-span-2">
          <label htmlFor="booking-name" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('booking:form.name')} *
          </label>
          <input
            id="booking-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('booking:form.namePlaceholder')}
            className={cn(inputBase, errors.name ? inputError : inputNormal)}
          />
          {errors.name && <p className="mt-1.5 text-sm text-red-400">{errors.name}</p>}
        </div>

        {/* Email */}
        <div className="min-w-0">
          <label htmlFor="booking-email" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('booking:form.email')} *
          </label>
          <input
            id="booking-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('booking:form.emailPlaceholder')}
            className={cn(inputBase, errors.email ? inputError : inputNormal)}
          />
          {errors.email && <p className="mt-1.5 text-sm text-red-400">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div className="min-w-0">
          <label htmlFor="booking-phone" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('booking:form.phone')}
          </label>
          <input
            id="booking-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('booking:form.phonePlaceholder')}
            className={cn(inputBase, inputNormal)}
          />
        </div>

        {/* Service */}
        <div className="min-w-0 md:col-span-2">
          <label htmlFor="booking-service" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('booking:form.service')}
          </label>
          <select
            id="booking-service"
            value={service}
            onChange={(e) => setService(e.target.value)}
            className={cn(inputBase, inputNormal, 'appearance-none')}
          >
            <option value="">{t('booking:form.servicePlaceholder')}</option>
            {serviceOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
            <option value="other">{t('booking:form.serviceOther')}</option>
          </select>
        </div>

        {/* Date */}
        <div className="min-w-0">
          <label htmlFor="booking-date" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('booking:form.date')}
          </label>
          <div className="relative flex min-w-0">
            <input
              id="booking-date"
              type="date"
              min={minDate}
              value={preferredDate}
              onChange={(e) => {
                const value = e.target.value
                if (value && isWeekend(value)) {
                  setDateError(t('booking:form.weekendError'))
                  return
                }
                setDateError(null)
                setPreferredDate(value)
              }}
              className={cn(inputBase, inputNormal)}
            />
          </div>
          {dateError && <p className="mt-1.5 text-sm text-red-400">{dateError}</p>}
        </div>

        {/* Time */}
        <div className="min-w-0">
          <label htmlFor="booking-time" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('booking:form.time')}
          </label>
          <select
            id="booking-time"
            value={preferredTime}
            onChange={(e) => setPreferredTime(e.target.value)}
            disabled={!preferredDate || timeSlots.length === 0}
            className={cn(
              inputBase,
              inputNormal,
              'appearance-none',
              (!preferredDate || timeSlots.length === 0) && 'opacity-60 cursor-not-allowed'
            )}
          >
            <option value="">
              {!preferredDate
                ? t('booking:form.timePlaceholder')
                : timeSlots.length === 0
                  ? t('booking:form.timeClosed')
                  : t('booking:form.timePlaceholder')}
            </option>
            {timeSlots.map((slot) => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div className="min-w-0 md:col-span-2">
          <label htmlFor="booking-notes" className="block text-xs font-medium uppercase tracking-wide text-white/60 mb-1.5">
            {t('booking:form.notes')}
          </label>
          <textarea
            id="booking-notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('booking:form.notesPlaceholder')}
            className={cn(inputBase, inputNormal, 'resize-none')}
          />
        </div>
      </div>

      {/* Privacy */}
      <div className="mt-6">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={privacy}
            onChange={(e) => setPrivacy(e.target.checked)}
            className={cn(
              'mt-1 h-4 w-4 rounded border bg-ink-deep/60 text-primary focus:ring-primary',
              errors.privacy ? 'border-red-400' : 'border-white/10'
            )}
          />
          <span className="text-sm text-white/70 group-hover:text-white/90 transition-colors">
            {t('booking:form.privacy')}{' '}
            <LocalizedLink to="privacy" className="text-primary hover:text-primary-light underline underline-offset-4">
              {t('booking:form.privacyLink')}
            </LocalizedLink>
            .
          </span>
        </label>
        {errors.privacy && <p className="mt-1.5 text-sm text-red-400">{errors.privacy}</p>}
      </div>

      {submitError && (
        <div className="mt-6 p-4 rounded-lg bg-red-500/10 border border-red-400/20">
          <p className="text-sm text-red-300">{submitError}</p>
        </div>
      )}

      <div className="mt-8">
        <Button
          type="submit"
          disabled={submitting}
          className="w-full md:w-auto"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t('booking:form.submitting')}
            </>
          ) : (
            t('booking:form.submit')
          )}
        </Button>
      </div>
    </form>
  )
}
